import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Composant récursif pour gérer les sous-menus imbriqués
 */
function NestedMenuItem({ option, hasSubMenu, onClose, MENU_BG, HOVER_BG }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [subMenuPosition, setSubMenuPosition] = useState({ top: 0, left: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const itemRef = useRef(null);
  const subMenuRef = useRef(null);

  const handleMouseEnter = () => {
    setIsHovered(true);
    
    if (hasSubMenu) {
      // Annuler la fermeture en cours
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
      
      // Ouvrir le sous-menu après un court délai
      const timeout = setTimeout(() => {
        if (itemRef.current) {
          // Calculer la position du sous-menu en coordonnées fixed
          const rect = itemRef.current.getBoundingClientRect();
          const margin = 10;
          const subMenuWidth = 250; // Estimation de la largeur du sous-menu
          const subMenuHeight = 300; // Estimation de la hauteur du sous-menu
          
          // Par défaut, ouvrir à droite juste après l'item parent
          let left = rect.right - 1; // -1 pour avoir un léger chevauchement visuel
          let top = rect.top;
          let openToLeft = false;
          
          // Si déborde à droite, ouvrir à gauche
          if (left + subMenuWidth > window.innerWidth - margin) {
            // Calculer pour que le bord droit du sous-menu s'aligne avec le bord gauche du menu parent
            left = rect.left - subMenuWidth + 1;
            openToLeft = true;
          }
          
          // Si déborde en bas, ajuster vers le haut
          if (top + subMenuHeight > window.innerHeight - margin) {
            const overflow = (top + subMenuHeight) - (window.innerHeight - margin);
            top = Math.max(margin, top - overflow);
          }
          
          // S'assurer qu'on ne dépasse pas en haut
          if (top < margin) {
            top = margin;
          }
          
          setSubMenuPosition({ top, left, openToLeft });
          setIsOpen(true);
        }
      }, 100); // Petit délai pour éviter l'ouverture accidentelle
      
      setHoverTimeout(timeout);
    }
  };

  const handleMouseLeave = (e) => {
    setIsHovered(false);
    
    if (hasSubMenu) {
      // Fermer après un délai pour laisser le temps d'aller au sous-menu
      const timeout = setTimeout(() => {
        setIsOpen(false);
      }, 200);
      setHoverTimeout(timeout);
    }
  };

  const handleSubMenuMouseEnter = () => {
    // Annuler la fermeture si on entre dans le sous-menu
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handleSubMenuMouseLeave = () => {
    // Fermer après un délai quand on quitte le sous-menu
    const timeout = setTimeout(() => {
      setIsOpen(false);
    }, 200);
    setHoverTimeout(timeout);
  };

  // Nettoyer le timeout au démontage
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  return (
    <div
      ref={itemRef}
      style={{ position: 'relative' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        style={{
          padding: '8px 16px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          backgroundColor: isHovered || isOpen ? HOVER_BG : 'transparent',
          transition: 'background-color 0.15s',
          userSelect: 'none',
        }}
        onMouseDown={(e) => {
          console.log(`⬇️ MouseDown sur NestedMenuItem: "${option.label}"`);
          console.log(`   hasSubMenu=${hasSubMenu}`);
          console.log(`   option.onClick existe:`, !!option.onClick);
          
          if (!hasSubMenu && option.onClick) {
            e.preventDefault();
            e.stopPropagation();
            console.log(`  ✅ Exécution du onClick via mouseDown pour "${option.label}"`);
            try {
              option.onClick();
              // Fermer après un petit délai pour s'assurer que l'action est traitée
              setTimeout(() => onClose(), 50);
            } catch (err) {
              console.error(`  ❌ Erreur lors de l'exécution du onClick:`, err);
            }
          }
        }}
        onClick={(e) => {
          // onClick comme backup au cas où mouseDown ne fonctionne pas
          if (!hasSubMenu && option.onClick) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {option.label}
        {hasSubMenu && <span style={{ float: 'right', marginLeft: '12px' }}>▶</span>}
      </div>

      {/* Sous-menu imbriqué avec portal */}
      {hasSubMenu && isOpen && createPortal(
        <div
          ref={subMenuRef}
          style={{
            position: 'fixed',
            top: `${subMenuPosition.top}px`,
            left: `${subMenuPosition.left}px`,
            backgroundColor: MENU_BG,
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            padding: '4px 0',
            zIndex: 10000,
            minWidth: '200px',
            maxHeight: '40vh',
            overflowY: 'auto',
            pointerEvents: 'auto',
          }}
          onMouseEnter={handleSubMenuMouseEnter}
          onMouseLeave={handleSubMenuMouseLeave}
          onClick={(e) => {
            console.log(`🖱️ Clic sur le sous-menu conteneur de "${option.label}"`);
          }}
        >
          {(option.subMenu || []).map((nestedOption, idx) => {
            const hasNestedSubMenu = Array.isArray(nestedOption?.subMenu) && nestedOption.subMenu.length > 0;
            console.log(`  Rendu item ${idx}: "${nestedOption.label}", hasSubMenu=${hasNestedSubMenu}, onClick exists:`, !!nestedOption.onClick);
            return (
              <NestedMenuItem
                key={idx}
                option={nestedOption}
                hasSubMenu={hasNestedSubMenu}
                onClose={onClose}
                MENU_BG={MENU_BG}
                HOVER_BG={HOVER_BG}
              />
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * ContextMenu
 *   - x, y : coordonnées (pour positionner le menu)
 *   - options : tableau d'objets { label, onClick, subMenu?: [ ... ] }
 *   - onClose : fonction pour fermer tout le menu
 *   - useFixed : booléen => si true, position: 'fixed' ; sinon 'absolute'
 */
export default function ContextMenu({ x, y, options, onClose, useFixed = false }) {
  /* Couleurs de référence */
  const MENU_BG = '#f9f9f9';
  const HOVER_BG = '#eee';

  // Indice du sous‑menu ouvert (un seul à la fois)
  const [activeSubMenuIndex, setActiveSubMenuIndex] = useState(null);

  // Timer de fermeture si on quitte le menu global
  const [hoverTimeout, setHoverTimeout] = useState(null);

  // Référence au conteneur principal
  const menuRef = useRef(null);

  // Référence au sous‑menu actuellement ouvert
  const subMenuRef = useRef(null);

  // Ombres de défilement (haut / bas)
  const [shadowTop, setShadowTop] = useState(false);
  const [shadowBottom, setShadowBottom] = useState(false);

  // Position sécurisée dans l'écran
  const [position, setPosition] = useState({ x, y });

  // Met à jour la position quand les props changent
  useEffect(() => {
    setPosition({ x, y });
  }, [x, y]);

  /**
   * Style global pour une scrollbar fine toujours visible.
   */
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .context-submenu::-webkit-scrollbar       { width: 6px; }
      .context-submenu::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
      .context-submenu::-webkit-scrollbar-thumb { background: #bdbdbd; border-radius: 3px; }
      .context-submenu { scrollbar-width: thin; scrollbar-color: #bdbdbd #f1f1f1; }
    `;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  /**
   * S'assure que le menu principal reste à l'intérieur de la fenêtre
   */
  useLayoutEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      let newX = position.x;
      let newY = position.y;

      const margin = 10;
      if (rect.right > window.innerWidth - margin) {
        newX = Math.max(margin, window.innerWidth - rect.width - margin);
      }
      if (rect.bottom > window.innerHeight - margin) {
        newY = Math.max(margin, window.innerHeight - rect.height - margin);
      }
      if (rect.left < margin) {
        newX = margin;
      }
      if (rect.top < margin) {
        newY = margin;
      }

      if (newX !== position.x || newY !== position.y) {
        setPosition({ x: newX, y: newY });
      }
    }
  }, [position, options]);

  /**
   * Ajuste dynamiquement la position du sous‑menu pour éviter tout dépassement
   * et met à jour les ombres indiquant qu'il est scrollable.
   */
  useLayoutEffect(() => {
    if (!subMenuRef.current) return;

    const el = subMenuRef.current;
    const margin = 10;

    // Réinitialise la position et la translation
    el.style.left = '100%';
    el.style.right = 'auto';
    el.style.transform = 'translateY(0px)';

    const rect = el.getBoundingClientRect();

    // Si le sous‑menu dépasse à droite, on l'ouvre à gauche
    if (rect.right > window.innerWidth - margin) {
      el.style.left = 'auto';
      el.style.right = '100%';
    }

    // Ajuste verticalement s'il dépasse en bas ou en haut
    let translateY = 0;
    if (rect.bottom > window.innerHeight - margin) {
      translateY = window.innerHeight - margin - rect.bottom;
    } else if (rect.top < margin) {
      translateY = margin - rect.top;
    }
    if (translateY !== 0) {
      el.style.transform = `translateY(${translateY}px)`;
    }

    // Initialise l'état des ombres
    updateScrollShadows(el);

    // Surveille le scroll pour mettre à jour les ombres
    function onScroll() {
      updateScrollShadows(el);
    }
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [activeSubMenuIndex]);

  /**
   * Réinitialise la couleur des items principaux quand le sous‑menu change
   */
  useEffect(() => {
    if (!menuRef.current) return;
    const items = menuRef.current.querySelectorAll('.context-item');
    items.forEach((el, idx) => {
      el.style.backgroundColor = idx === activeSubMenuIndex ? HOVER_BG : 'transparent';
    });
  }, [activeSubMenuIndex]);

  /**
   * Met à jour l'affichage des ombres haut/bas selon la position du scroll
   */
  function updateScrollShadows(el) {
    if (!el) return;
    setShadowTop(el.scrollTop > 0);
    setShadowBottom(el.scrollTop + el.clientHeight < el.scrollHeight);
  }

  // Fermer si on clique n'importe où en dehors
  useEffect(() => {
    function handleDocumentClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [onClose]);

  /**
   * Annule un timer de fermeture en cours
   */
  function clearHoverTimeout() {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }

  /**
   * Lance un timer de 0,5 s pour fermer tout sous-menu
   * (quand la souris quitte entièrement le menu)
   */
  function closeAllWithDelay() {
    const timer = setTimeout(() => {
      setActiveSubMenuIndex(null);
    }, 500);
    setHoverTimeout(timer);
  }

  /**
   * Ouvrir immédiatement un sous-menu (ferme l’ancien si besoin)
   */
  function openSubMenu(index) {
    clearHoverTimeout();
    setActiveSubMenuIndex(index);
  }

  /**
   * Griser (hover) un item principal au survol
   */
  function handleItemMouseEnter(e) {
    e.currentTarget.style.backgroundColor = HOVER_BG;
  }

  /**
   * Dé‑griser un item principal s'il n'a pas de sous‑menu actif
   */
  function handleItemMouseLeave(e, isSubMenuOpen) {
    if (!isSubMenuOpen) {
      e.currentTarget.style.backgroundColor = 'transparent';
    }
    // Si le sous‑menu est ouvert, on le laisse en gris
  }

  return (
    <div
      ref={menuRef}
      style={{
        position: useFixed ? 'fixed' : 'absolute',
        top: position.y,
        left: position.x,
        backgroundColor: MENU_BG,
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '4px 0',
        zIndex: 1000,
        maxHeight: 'calc(100vh - 10px)',
        overflow: 'visible',
      }}
      onMouseEnter={clearHoverTimeout} // On reste dans le menu => annule la fermeture
      onMouseLeave={closeAllWithDelay} // On sort du menu => ferme tout après 0,5 s
    >
      {(options || []).map((option, index) => {
        // Support pour les séparateurs
        if (option.type === 'separator') {
          return (
            <div
              key={index}
              style={{
                height: '1px',
                backgroundColor: '#ddd',
                margin: '4px 8px',
              }}
            />
          );
        }

        const hasSubMenu = Array.isArray(option?.subMenu) && option.subMenu.length > 0;
        const isSubMenuOpen = activeSubMenuIndex === index;

        return (
          <div
            key={index}
            style={{ position: 'relative' }}
            onMouseEnter={(e) => {
              handleItemMouseEnter(e);
              if (hasSubMenu) {
                openSubMenu(index); // ouvre immédiatement
              }
            }}
            onMouseLeave={(e) => {
              handleItemMouseLeave(e, isSubMenuOpen);
            }}
          >
            {/* ITEM PRINCIPAL */}
            <div
              className="context-item"
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                backgroundColor: isSubMenuOpen ? HOVER_BG : 'transparent',
              }}
              onClick={() => {
                if (!hasSubMenu) {
                  option.onClick?.();
                  onClose();
                }
              }}
            >
              {option.label}
              {hasSubMenu && <span style={{ float: 'right' }}>▶</span>}
            </div>

            {/* Sous‑menu si ouvert */}
            {hasSubMenu && isSubMenuOpen && (
              <div
                ref={subMenuRef} /* Référence pour ajustements dynamiques */
                className="context-submenu"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '100%',
                  marginLeft: '0px',
                  backgroundColor: MENU_BG,
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '6px',
                  boxShadow: [
                    '0 4px 12px rgba(0, 0, 0, 0.15)',
                    shadowTop ? 'inset 0 6px 6px -6px rgba(0,0,0,0.18)' : null,
                    shadowBottom ? 'inset 0 -6px 6px -6px rgba(0,0,0,0.18)' : null,
                  ]
                    .filter(Boolean)
                    .join(', '),
                  padding: '4px 0',
                  zIndex: 1001,
                  maxHeight: '40vh',
                  overflowY: 'auto',
                  overflowX: 'visible',
                }}
              >
                {(option.subMenu || []).map((subOption, subIndex) => {
                  const hasNestedSubMenu = Array.isArray(subOption?.subMenu) && subOption.subMenu.length > 0;
                  return (
                    <NestedMenuItem
                      key={subIndex}
                      option={subOption}
                      hasSubMenu={hasNestedSubMenu}
                      onClose={onClose}
                      MENU_BG={MENU_BG}
                      HOVER_BG={HOVER_BG}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
